import { useZero } from "@rocicorp/zero/react";
import { useCallback, useMemo, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import type { GrantEntry } from "../../shared/rbac/roles";
import { GrantSpaceAccessModal } from "../components/GrantSpaceAccessModal";
import { grantForMutation } from "../lib/grantAssembly";
import type { SupplierGap } from "../lib/spaceMemberSupplierGap";
import { userDisplayName } from "../lib/userDisplayName";
import { useQuery } from "./useQuery";

type Pending = {
  spaceName: string;
  gaps: SupplierGap[];
  proceed: () => void;
};

// existing ∪ additions, deduped by principal+role — the same union
// ScopeAccessTable.commitAll builds, since setScopeRoleGrants replaces the whole
// grant list for a scope.
function unionGrants(
  existing: readonly GrantEntry[],
  additions: readonly GrantEntry[]
): GrantEntry[] {
  const seen = new Set<string>();
  const out: GrantEntry[] = [];
  for (const g of [...existing, ...additions]) {
    const principal = g.userGroupId ?? g.userId ?? g.serviceAccountId ?? "";
    const role = g.builtinRoleId ?? g.customRoleId ?? "";
    const key = `${principal}|${role}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(g);
  }
  return out;
}

// Pre-flights adding a member to a space: if they'd lack any connector/skill the
// space uses, opens a modal to grant those the admin can share, then runs the
// original member-add. `guard` runs the add directly when there's no gap.
export function useGrantSpaceAccess() {
  const z = useZero();
  const [allUsers] = useQuery(queries.allUsers());
  const [allGroups] = useQuery(queries.selectableUserGroups());
  const [pending, setPending] = useState<Pending | null>(null);

  const userNames = useMemo(
    () => new Map(allUsers.map((u) => [u.id, userDisplayName(u)] as const)),
    [allUsers]
  );
  const groupNames = useMemo(
    () => new Map(allGroups.map((g) => [g.id, g.name] as const)),
    [allGroups]
  );

  // Returns true when it opened the modal (commit deferred until confirm), so the
  // caller can keep the member staged in the list behind it.
  const guard = useCallback(
    (args: { spaceName: string; gaps: SupplierGap[]; proceed: () => void }) => {
      if (args.gaps.length === 0) {
        args.proceed();
        return false;
      }
      setPending(args);
      return true;
    },
    []
  );

  const onConfirm = useCallback(() => {
    if (!pending) return;
    const grantable = pending.gaps.filter((g) => g.canShare);
    // Grant every supplier before the member-add fires: Zero applies a client's
    // mutations in submission order, so the add's cross-resource guard sees the
    // new grants and passes.
    for (const gap of grantable) {
      z.mutate(
        sharedMutators.setScopeRoleGrants({
          scopeId: gap.supplierId,
          scopeKind: gap.scopeKind,
          grants: unionGrants(gap.existingGrants, gap.additions).map(
            grantForMutation
          ),
        })
      );
    }
    // Only finish adding the member when nothing is left blocked — otherwise the
    // add would still fail server-side on the items the admin can't share.
    if (grantable.length === pending.gaps.length) pending.proceed();
    setPending(null);
  }, [z, pending]);

  const modal = pending ? (
    <GrantSpaceAccessModal
      open
      onClose={() => setPending(null)}
      spaceName={pending.spaceName}
      gaps={pending.gaps}
      userNames={userNames}
      groupNames={groupNames}
      onConfirm={onConfirm}
    />
  ) : null;

  return { guard, modal };
}
